import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  Grid,
  Popover,
  useMediaQuery,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import dayjs from "dayjs";
import "dayjs/locale/en";
import { useDispatch, useSelector } from "react-redux";
import {
  updateActivityDuration,
  updateReservationType,
  updateSelectedDate,
  updateTime,
} from "../../store/slice/Activity.slice";
import { useTranslation } from "react-i18next";
import { useTheme } from "@emotion/react";

const Calendar = ({ isOpen, handleClose, Ref }) => {
  const anchorEl = Ref;
  const { css } = useSelector((store) => store?.widgetSettings);
  const dateCss = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Date"
  )?.fields;
  const theme = useTheme();
  const { t } = useTranslation();
  const { blockedDates, languageWords } = useSelector(
    (store) => store?.Activity
  );
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const today = dayjs();
  const [currentDate, setCurrentDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState(null);
  const daysOfWeek = [
    languageWords?.monday?.slice(0, 2),
    languageWords?.Tuesday?.slice(0, 2),
    languageWords?.Wednesday?.slice(0, 2),
    languageWords?.Thursday?.slice(0, 2),
    languageWords?.Friday?.slice(0, 2),
    languageWords?.Saturday?.slice(0, 2),
    languageWords?.Sunday?.slice(0, 2),
  ];
  const [anchorOriginVert, setAnchorOriginVert] = useState("bottom");
  const [transformOriginVert, setTransformOriginVert] = useState("top");
  const daysInMonth = currentDate.daysInMonth();
  const firstDayOfMonth = (currentDate.startOf("month").day() + 6) % 7;
  const dispatch = useDispatch();
  const formattedBlockedDates = blockedDates?.map((date) =>
    dayjs(date).format("YYYY-MM-DD")
  );
  const isPastDay = (day) => currentDate.date(day).isBefore(today, "day");
  const isBlockedDate = (day) =>
    formattedBlockedDates?.includes(currentDate.date(day).format("YYYY-MM-DD"));
  const isToday = (day) => today.isSame(currentDate.date(day), "day");
  const isSelected = (day) =>
    selectedDate && selectedDate.isSame(currentDate.date(day), "day");

  const handlePrevMonth = () => setCurrentDate(currentDate.subtract(1, "month"));
  const handleNextMonth = () => setCurrentDate(currentDate.add(1, "month"));

  const handleDateSelect = (day) => {
    if (!isPastDay(day) && !isBlockedDate(day)) {
      const newSelectedDate = currentDate.date(day);
      setSelectedDate(newSelectedDate);
      const formattedDate = newSelectedDate.format("DD-MM-YYYY");
      dispatch(updateSelectedDate(formattedDate));
      resetRelatedField();
      handleClose();
    }
  };

  const resetRelatedField = () => {
    dispatch(updateReservationType({}));
    dispatch(updateTime(""));
    dispatch(updateActivityDuration(""));
  };

  const getTranslatedMonth = () => {
    const monthName = currentDate?.format("MMMM");
    return languageWords?.[monthName] || monthName;
  };

  useEffect(() => {
    if (!isOpen || !anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const POPOVER_HEIGHT = 430;
    if (rect.bottom + POPOVER_HEIGHT > viewportHeight) {
      setAnchorOriginVert("top");
      setTransformOriginVert("bottom");
    } else {
      setAnchorOriginVert("bottom");
      setTransformOriginVert("top");
    }
  }, [isOpen, anchorEl]);

  return (
    <Popover
      open={isOpen && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: anchorOriginVert, horizontal: "center" }}
      transformOrigin={{ vertical: transformOriginVert, horizontal: "center" }}
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      PaperProps={{
        sx: {
          width: isSmallScreen ? "92vw" : isMediumScreen ? "80%" : "80%", 
          maxWidth: "400px",
          borderRadius: 2,
          boxShadow: 3,
          p: isSmallScreen ? 1 : 2,
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: dateCss?.CalendarBackgroundColor,
          padding: isSmallScreen ? 0.5 : 2,
          textAlign: "center",
        }}
      >
        {/* ── Year Navigation ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: isSmallScreen ? 0.5 : 2,
            py: isSmallScreen ? 0.5 : 1,
          }}
        >
          <IconButton
            onClick={handlePrevMonth}
            size={isSmallScreen ? "small" : "medium"} 
            sx={{
              color: dateCss?.CalendarArrowColor,
              "&:hover": { color: dateCss?.CalendarArrowColorHover },
              "&:active": { color: dateCss?.CalendarArrowColorActive },
              "&.Mui-disabled": { color: theme?.calender?.disabledArrowColor },
            }}
          >
            <ArrowBack fontSize={isSmallScreen ? "small" : "medium"} />
          </IconButton>

          <Typography
            sx={{
              fontWeight: "bold",
              color: dateCss?.CalendarYearTextColor,
              fontSize: isSmallScreen ? "14px" : "20px",
            }}
          >
            {currentDate.format("YYYY")}
          </Typography>

          <IconButton
            onClick={handleNextMonth}
            size={isSmallScreen ? "small" : "medium"}
            sx={{
              color: dateCss?.CalendarArrowColor,
              "&:hover": { color: dateCss?.CalendarArrowColorHover },
              "&:active": { color: dateCss?.CalendarArrowColorActive },
              "&.Mui-disabled": { color: theme?.calender?.disabledArrowColor },
            }}
          >
            <ArrowForward fontSize={isSmallScreen ? "small" : "medium"} />
          </IconButton>
        </Box>

        {/* ── Month Name ── */}
        <Typography
          sx={{
            mb: isSmallScreen ? 0.5 : 1,
            color: dateCss?.CalendarMonthTextColor,
            fontSize: isSmallScreen ? "16px" : "24px", 
            fontWeight: "bold",
          }}
        >
          {getTranslatedMonth()}
        </Typography>

        {/* ── Day Headers ── */}
        <Grid container spacing={isSmallScreen ? 0.5 : 1} mt={isSmallScreen ? 1 : 3}>
          {daysOfWeek.map((day, index) => (
            <Grid item xs={1.71} key={index}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  color: dateCss?.CalendarDaysTextColor,
                  fontSize: isSmallScreen ? "0.65rem" : "0.9rem", 
                  textAlign: "center",
                }}
              >
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* ── Date Grid ── */}
        <Grid container spacing={isSmallScreen ? 0.5 : 1} sx={{ mt: 0.5 }}>
          {[...Array(firstDayOfMonth)].map((_, index) => (
            <Grid item xs={1.71} key={index} />
          ))}
          {[...Array(daysInMonth)].map((_, dayIndex) => {
            const day = dayIndex + 1;
            const isPast = isPastDay(day);
            const isBlocked = isBlockedDate(day);
            const isSelectedDay = isSelected(day);
            const isActive = !isPast && !isBlocked;

            return (
              <Grid item xs={1.71} key={dayIndex}>
                <Box
                  onClick={() => isActive && handleDateSelect(day)}
                  sx={{
                    border: "2px dotted",
                    borderColor:
                      isBlocked || isPast
                        ? dateCss?.CalendarDateInactiveBorderColor
                        : dateCss?.CalendarDateBorderColor,
                    backgroundColor:
                      isBlocked || isPast
                        ? theme?.widget?.disabledBgClr ?? dateCss?.CalendarDateInactiveBackgroundColor
                        : isSelectedDay && isActive
                        ? dateCss?.CalendarDateBackgroundColorActive
                        : dateCss?.CalendarDateBackgroundColor,
                    cursor: isActive ? "pointer" : "default",
                    borderRadius: isSmallScreen ? "6px" : "8px",
                    textAlign: "center",
                    transition: "background-color 0.2s ease",
                   
                    width: isSmallScreen ? "30px" : "40px",
                    height: isSmallScreen ? "30px" : "40px",
                    fontWeight: isActive ? "bold" : "normal",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mx: "auto", 
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: isSelectedDay
                        ? theme?.calender?.dateTextColorActive ?? "#fff"
                        : isBlocked || isPast
                        ? theme?.widget?.disableTextColor ?? "grey"
                        : theme?.calender?.dateTextColor,
                      fontSize: isSmallScreen ? "12px" : "16px",
                      fontWeight: isActive ? 600 : 400,
                      lineHeight: 1,
                    }}
                  >
                    {day}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Popover>
  );
};

export default Calendar;